// FUNCTIONALITY-101/102 / CROSSSELL-109/110 / COMMERCE-LAUNCH-167A-R10 — Store V2 product list.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import { ProductCard, EmptyState } from "./components.js";
import { IsolatedSearchBar } from "./IsolatedSearchBar.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  catalogSortOptions,
  normalizeCatalogSortKey,
  sortCatalogProducts,
  type CatalogSortKey,
} from "./catalog-sort-v2.js";
import {
  readCollectionNavState,
  writeCollectionNavState,
} from "./collection-nav-state-v2.js";

const PAGE_SIZE = 24;

/** Explicit pagination contract: total is full list length; page loads PAGE_SIZE. */
export const CATALOG_LIST_PAGE_SIZE = PAGE_SIZE;

/** @deprecated prefer CatalogSortKey */
export type SortKey = CatalogSortKey | "default" | "name" | "stock";

export interface CatalogListV2Props {
  lang: StoreV2Lang;
  heading: string;
  /** COMMERCE-176C-R1 — once-per-collection secondary currency note. */
  currencyNote?: string;
  products: CatalogV2Product[];
  titleOf: (p: CatalogV2Product) => string;
  emptyTitle: string;
  emptyHint?: string | undefined;
  onOpenProduct: (id: string, seed?: CatalogV2Product) => void;
  onPrefetchProduct?: (product: CatalogV2Product) => void;
  onAdd: (product: CatalogV2Product) => void;
  /** MERCH-129A-R1 — open canonical cart (navigation only). */
  onOpenCart: () => void;
  cartItemCount?: number;
  sourceSurface?: string;
  sourceRoute?: string;
  categoryId?: string | null;
  rankingVersion?: string;
  onSearch?: (query: string) => void;
  /** R7 — same photo-search affordance beside list search. */
  visualSearchSlot?: ReactNode;
  /** 168D5 — local autocomplete catalog. */
  searchableProducts?: import("./search-v2.js").SearchableProduct[];
  suggestCloseToken?: number;
  searchBarEpoch?: number;
  onOpenProductFromSuggest?: (productId: string, seed?: CatalogV2Product) => void;
  onOpenBrandFromSuggest?: (brandName: string) => void;
  qtyOf?: (productId: string) => number;
  onInc?: (productId: string) => void;
  onDec?: (productId: string) => void;
  onAvailabilityRequest?: (product: CatalogV2Product) => void;
  /** COMMERCE-176D-R1 recovery — stable key for sort/limit persistence across remount. */
  collectionKey?: string;
  /**
   * CUSTOMER-R4.15-C — remote incremental category pages.
   * When set, load-more fetches next server/static page instead of slicing a giant in-memory list.
   * CUSTOMER-R4.15-E — products are already globally sorted for `remoteSort`; do not re-sort locally.
   */
  totalCount?: number;
  hasMoreRemote?: boolean;
  loadingMore?: boolean;
  onLoadMoreRemote?: () => void;
  /** Server/static sort key currently loaded (remote shards). */
  remoteSort?: CatalogSortKey;
  /** When remote: changing sort reloads page 1 for the full category. */
  onRemoteSortChange?: (sort: CatalogSortKey) => void;
  /** First page still loading — show shell, not empty-state. */
  initialLoading?: boolean;
  /** First-page failure with zero products. */
  loadError?: string | null;
  onRetryLoad?: () => void;
}

function resolveCollectionKey(props: {
  collectionKey?: string | undefined;
  sourceRoute?: string | undefined;
  categoryId?: string | null | undefined;
  heading: string;
}): string {
  const explicit = String(props.collectionKey ?? "").trim();
  if (explicit) return explicit;
  const route = String(props.sourceRoute ?? "").trim();
  if (route) return route;
  const cat = props.categoryId == null ? "" : String(props.categoryId).trim();
  if (cat) return `category:${cat}`;
  return String(props.heading ?? "").trim() || "catalog";
}

export function CatalogListV2({
  lang,
  heading,
  currencyNote,
  products,
  titleOf,
  emptyTitle,
  emptyHint,
  onOpenProduct,
  onPrefetchProduct,
  onAdd,
  onOpenCart,
  cartItemCount = 0,
  sourceSurface = "catalog",
  sourceRoute,
  categoryId = null,
  rankingVersion = "catalog_list_v1",
  onSearch,
  visualSearchSlot,
  searchableProducts: _searchableProducts,
  suggestCloseToken = 0,
  searchBarEpoch = 0,
  onOpenProductFromSuggest,
  onOpenBrandFromSuggest,
  qtyOf,
  onInc,
  onDec,
  onAvailabilityRequest,
  collectionKey,
  totalCount,
  hasMoreRemote,
  loadingMore = false,
  onLoadMoreRemote,
  remoteSort,
  onRemoteSortChange,
  initialLoading = false,
  loadError = null,
  onRetryLoad,
}: CatalogListV2Props) {
  const remoteMode = typeof hasMoreRemote === "boolean" && typeof onLoadMoreRemote === "function";
  const navKey = resolveCollectionKey({ collectionKey, sourceRoute, categoryId, heading });
  const [limit, setLimit] = useState(() => {
    const saved = readCollectionNavState(navKey, PAGE_SIZE);
    return saved?.limit ?? PAGE_SIZE;
  });
  const [sort, setSort] = useState<CatalogSortKey>(() => {
    if (remoteMode && remoteSort) return normalizeCatalogSortKey(remoteSort);
    const saved = readCollectionNavState(navKey, PAGE_SIZE);
    return saved?.sort ?? "recommended";
  });
  const options = catalogSortOptions(lang);

  useEffect(() => {
    if (!remoteMode || !remoteSort) return;
    const next = normalizeCatalogSortKey(remoteSort);
    setSort((prev) => (prev === next ? prev : next));
  }, [remoteMode, remoteSort]);

  useEffect(() => {
    writeCollectionNavState(navKey, {
      collectionKey: navKey,
      sort,
      limit,
    });
  }, [navKey, sort, limit]);

  const remoteTrusted = remoteMode && typeof onRemoteSortChange === "function";
  const sorted = useMemo(
    () => (remoteTrusted ? products : sortCatalogProducts(products, sort, titleOf)),
    [products, sort, titleOf, remoteTrusted],
  );
  const visible = remoteTrusted ? sorted : sorted.slice(0, limit);
  const displayTotal = typeof totalCount === "number" ? totalCount : products.length;
  const canLoadMore = remoteMode ? Boolean(hasMoreRemote) : limit < sorted.length;

  // CUSTOMER-R4.15 — onSearch must open GLOBAL_CATALOG (StoreV2App.openSearch).
  // Do not locally filter by new arrivals / category / brand for normal search.
  const searchBar = onSearch ? (
    <IsolatedSearchBar
      lang={lang}
      onSubmit={(q) => {
        const t = q.trim();
        if (t.length >= 2) onSearch(t);
      }}
      onOpenProduct={(id, seed) => {
        (onOpenProductFromSuggest ?? onOpenProduct)(id, seed as CatalogV2Product | undefined);
      }}
      {...(onOpenBrandFromSuggest ? { onOpenBrand: onOpenBrandFromSuggest } : {})}
      visualSlot={visualSearchSlot}
      forceCloseToken={suggestCloseToken}
      sessionEpoch={searchBarEpoch}
    />
  ) : null;

  if (initialLoading && products.length === 0) {
    return (
      <div className="sv2-body sv2-route-top" data-route-top="catalog" data-testid="sv2-catalog-initial-loading">
        <div className="sv2-section-title">{heading}</div>
        {currencyNote ? (
          <p className="sv2-muted sv2-currency-note" data-testid="sv2-currency-note">
            {currencyNote}
          </p>
        ) : null}
        {searchBar}
        <div className="sv2-grid sv2-catalog-skeleton" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sv2-card sv2-card-skeleton" data-testid="sv2-catalog-skeleton-card" />
          ))}
        </div>
        <p className="sv2-muted" style={{ textAlign: "center", marginTop: 12 }}>
          {lang === "en" ? "Loading products…" : "Завантаження товарів…"}
        </p>
      </div>
    );
  }

  if (loadError && products.length === 0) {
    return (
      <div className="sv2-body" data-testid="sv2-catalog-load-error">
        <div className="sv2-section-title">{heading}</div>
        {searchBar}
        <EmptyState
          emoji="⚠️"
          title={lang === "en" ? "Could not load category" : "Не вдалося завантажити категорію"}
          hint={loadError}
        />
        {onRetryLoad ? (
          <button type="button" className="sv2-cta sv2-secondary" onClick={onRetryLoad} data-testid="sv2-catalog-retry">
            {lang === "en" ? "Retry" : "Спробувати ще раз"}
          </button>
        ) : null}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="sv2-body">
        <div className="sv2-section-title">{heading}</div>
        {currencyNote ? (
          <p className="sv2-muted sv2-currency-note" data-testid="sv2-currency-note">
            {currencyNote}
          </p>
        ) : null}
        {searchBar}
        <EmptyState emoji="🔍" title={emptyTitle} hint={emptyHint} />
      </div>
    );
  }

  const remaining = remoteMode
    ? Math.max(0, displayTotal - products.length)
    : Math.max(0, sorted.length - limit);

  return (
    <div className="sv2-body sv2-route-top" data-route-top="catalog" data-incremental={remoteMode ? "1" : "0"}>
      <div className="sv2-section-title">{heading}</div>
      {currencyNote ? (
        <p className="sv2-muted sv2-currency-note" data-testid="sv2-currency-note">
          {currencyNote}
        </p>
      ) : null}
      <div className="sv2-sticky-tools">
        {searchBar}
        <div className="sv2-listbar">
          <span className="sv2-muted" data-testid="sv2-catalog-total-count">
            {displayTotal} {lang === "en" ? "items" : "товарів"}
            {visible.length < displayTotal
              ? lang === "en"
                ? ` · showing ${visible.length}`
                : ` · показано ${visible.length}`
              : ""}
          </span>
          <label className="sv2-sort">
            <span className="sv2-sort-label">{lang === "en" ? "Sort" : "Сортування"}</span>
            <select
              value={sort}
              data-testid="sv2-catalog-sort"
              onChange={(e) => {
                const next = normalizeCatalogSortKey(e.target.value);
                setSort(next);
                if (remoteMode && onRemoteSortChange) {
                  onRemoteSortChange(next);
                  return;
                }
                if (!remoteMode) setLimit(PAGE_SIZE);
              }}
            >
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div className="sv2-grid">
        {visible.map((p, index) => (
          <ProductCard
            key={p.id}
            lang={lang}
            product={p}
            displayTitle={titleOf(p)}
            onOpen={(id) => onOpenProduct(id, p)}
            {...(onPrefetchProduct ? { onPrefetch: onPrefetchProduct } : {})}
            onAdd={onAdd}
            onOpenCart={onOpenCart}
            cartItemCount={cartItemCount}
            cardPosition={index}
            sourceSurface={sourceSurface}
            sourceRoute={sourceRoute ?? sourceSurface}
            categoryId={categoryId}
            rankingVersion={rankingVersion}
            qtyInCart={qtyOf?.(p.id) ?? 0}
            {...(onInc && onDec ? { onInc, onDec } : {})}
            {...(onAvailabilityRequest ? { onAvailabilityRequest } : {})}
          />
        ))}
      </div>
      {canLoadMore ? (
        <button
          type="button"
          className="sv2-loadmore"
          data-testid="sv2-catalog-load-more"
          disabled={loadingMore}
          onClick={() => {
            if (remoteMode) onLoadMoreRemote?.();
            else setLimit((l) => l + PAGE_SIZE);
          }}
        >
          {loadingMore
            ? lang === "en"
              ? "Loading…"
              : "Завантаження…"
            : lang === "en"
              ? `Show more (${remaining})`
              : `Показати ще (${remaining})`}
        </button>
      ) : null}
    </div>
  );
}
